/**
 * vialCount.js — resolve the actual physical vial count for an order line.
 *
 * Hierarchy (structured-first, no fabrication):
 *   1. Line-item `vial_count` (Select ladder writes this at checkout)
 *   2. Line-item `variant` string ("1vial"/"3vial"/"5vial") — Select tier
 *   3. Line-item `is_byok_kit` + `byok_items[].quantity` × `byok_size`
 *   4. Product-doc `kit_vial_count` (Supply fixed-tier products)
 *   5. Line-item `unit_label` regex ("3 Vials", "10 vials/kit") — legacy fallback
 *   6. null → UI must render "—", never zero or an inferred number
 */

function _parseVariantVials(variant) {
  const v = String(variant || "").toLowerCase().trim();
  if (v === "1vial") return 1;
  if (v === "3vial") return 3;
  if (v === "5vial") return 5;
  if (v === "10vial") return 10;
  return null;
}

function _parseUnitLabelVials(unit_label) {
  const s = String(unit_label || "").toLowerCase();
  // "3 vials", "3-vial kit", "10 vials/kit"
  const m = s.match(/(\d+)\s*[- ]?\s*vial/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n > 0 && n < 1000) return n;
  }
  return null;
}

/**
 * vialsPerUnit — vials in ONE unit (kit) of this line. Does NOT multiply by qty.
 * Returns null when it can't be determined from structured data.
 */
export function vialsPerUnit(item, productsById = {}) {
  if (!item) return null;
  // 1) explicit stored field
  if (typeof item.vial_count === "number" && item.vial_count > 0) return item.vial_count;
  // 2) Select variant slug
  const fromVariant = _parseVariantVials(item.variant);
  if (fromVariant) return fromVariant;
  // 3) BYOK: kit-size wins as one physical unit
  if (item.is_byok_kit) {
    if (typeof item.byok_size === "number" && item.byok_size > 0) return item.byok_size;
    if (Array.isArray(item.byok_items) && item.byok_items.length) {
      const sum = item.byok_items.reduce((s, b) => s + (Number(b?.quantity) || 0), 0);
      if (sum > 0) return sum;
    }
  }
  // 4) product-doc lookup (Supply fixed-tier stores kit_vial_count)
  const prod = productsById[item.product_id];
  if (prod && typeof prod.kit_vial_count === "number" && prod.kit_vial_count > 0) {
    return prod.kit_vial_count;
  }
  // 5) legacy unit_label regex — last resort
  const fromLabel = _parseUnitLabelVials(item.unit_label);
  if (fromLabel) return fromLabel;
  return null;
}

/**
 * totalVialsForLine — vials for the entire line (per-unit × qty). null if unknown.
 */
export function totalVialsForLine(item, productsById = {}) {
  const per = vialsPerUnit(item, productsById);
  if (per == null) return null;
  const qty = Number(item?.quantity) || 0;
  return per * qty;
}

/**
 * totalVialsForOrder — sum across every line. null if ANY line couldn't be
 * resolved (so the UI can honestly show "—" instead of an undercount).
 */
export function totalVialsForOrder(order, productsById = {}) {
  const items = order?.items || [];
  if (!items.length) return 0;
  let sum = 0;
  for (const it of items) {
    const v = totalVialsForLine(it, productsById);
    if (v == null) return null;
    sum += v;
  }
  return sum;
}

/** Human-readable format label for the items expansion table. */
export function formatLabelForItem(item, productsById = {}) {
  const per = vialsPerUnit(item, productsById);
  if (item?.is_byok_kit) return `BYOK · ${per || "?"} vials`;
  if (per === 1) return "1 Vial";
  if (per) return `${per}-Vial Kit`;
  // fall back to whatever the line stored, or dash
  return (item?.unit_label && String(item.unit_label).trim()) || "—";
}

/** Strength label — surface stored variant_mg first, then dose_mg from product. */
export function strengthForItem(item, productsById = {}) {
  const mg = String(item?.variant_mg || "").trim();
  if (mg) return `${mg} mg`;
  const prod = productsById[item?.product_id];
  const pMg = String(prod?.dose_mg || "").trim();
  if (pMg) return pMg.match(/mg$/i) ? pMg : `${pMg} mg`;
  const pStrength = String(prod?.kit_vial_strength_mg || "").trim();
  if (pStrength) return `${pStrength} mg`;
  return "—";
}
